<template>
    <flex-element :element-type="elementType">
        <div v-if="!dataSourceNew" style="padding: 10px;">
            Please select a data source.
        </div>

        <component
            :is="asyncGoodTable"
            v-if="asyncGoodTable"
            :key="componentKey"
            :class="tableClasses"
            :columns="columns"
            :rows="rows"
            :pagination-options="paginationOptions"
            :sort-options="sortOptions"
            row-style-class="bar"
        >
            <template slot="table-column" slot-scope="props">
                <div v-html="getAbsolutePathUrl(props.column.label)" />
            </template>
        </component>
    </flex-element>
</template>

<script>
import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import { getAbsolutePathUrl } from '@/scripts/links';
import tableItemsQuery from '@/graphql/queries/TableItems.gql';

export default {
    name: 'table-v1',
    mixins: [baseOrganism],

    apollo: {
        itemData: {
            query: tableItemsQuery,

            variables() {
                return {
                    dataSourceId: this.dataSourceNew,
                };
            },

            update(data) {
                if (!data || !data.item) return [];
                return data.item;
            },
        },
    },

    props: {
        displayName: {
            type: String,
            default: 'Table v1',
        },

        dataSource: {
            type: String,
            default: '',
        },

        activeIndex: {
            type: Number,
            default: -1,
        },

        tag: {
            type: String,
            default: 'table',
        },
    },

    data() {
        return {
            asyncGoodTable: null,
            columnDataType: {
                Checkbox: 'Checkbox',
                DateTime: 'DateTime',
                Links: 'Links',
                Text: 'Text',
            },
            cssDisplay: 'block',
            columns: [],
            rows: [],
            isRowHeading: false,
            paginationOptions: {
                enabled: false,
                mode: 'pages',
            },
            sortOptions: {
                enabled: false,
            },
            getAbsolutePathUrl,
            componentKey: 0,
            hidePastDates: false,
        };
    },

    computed: {
        hasShading() {
            return this.hasElementData('row-shading');
        },

        tableClasses() {
            const classes = ['o-table'];

            if (this.hasShading) classes.push('o-table--shading');

            return classes;
        },

        dataSourceNew() {
            return this.getElementData('table-data-source') || '';
        },
    },

    watch: {
        itemData() {
            this.setTableOptions();
            this.setColumn();
            this.setRow();

            // this is to fix the rendering of the pagination
            // changing 'pagination.perPage' for some reason
            // does not update the pagination items per page
            this.forceRerender();
        },

        async dataSourceNew() {
            const { VueGoodTable } = await import(/* webpackChunkName: "vue-good-table"  */'vue-good-table');
            await import('vue-good-table/dist/vue-good-table.css');

            this.asyncGoodTable = VueGoodTable;
        },
    },

    created() {
        this.setDataSource();
    },

    methods: {
        setDataSource() {
            if (this.dataSource && !this.dataSourceNew) {
                this.setElementData('table-data-source', this.dataSource, { triggerChangeDetection: false });
            }
        },

        getFieldValue(item, fieldName) {
            if (!item || !item.fields) return null;

            const filterResult = item.fields.filter(function (i) {
                return i.name === fieldName;
            });
            if (filterResult.length < 1) return null;
            return filterResult[0].value;
        },

        setTableOptions() {
            // pagination
            const pagination = this.getFieldValue(this.itemData, 'Pagination');

            this.paginationOptions.enabled = pagination === '1';
            if (this.paginationOptions.enabled) {
                const perPage = this.getFieldValue(this.itemData, 'Items Per Page');
                this.paginationOptions.perPage = parseInt(perPage, 10);
            }

            // sort
            const sortEnabled = this.getFieldValue(this.itemData, 'Enable Sort');
            const sortDefaultColumn = this.getFieldValue(this.itemData, 'Default Sort Column');
            const sortDefaultDirection = this.getFieldValue(this.itemData, 'Default Sort Direction');

            this.sortOptions.enabled = sortEnabled === '1';
            if (this.sortOptions.enabled && sortDefaultColumn && sortDefaultDirection) {
                this.sortOptions.initialSortBy = {
                    field: `column${ sortDefaultColumn }`,
                    type: sortDefaultDirection.toLowerCase(),
                };
            }

            this.hidePastDates = this.getFieldValue(this.itemData, 'Hide Past Dates') === '1';
        },

        setColumn() {
            const workingColumns = [];

            if (this.itemData) {
                for (let i = 1; i <= 10; i++) {
                    const columnName = this.getFieldValue(this.itemData, `Column Name ${ i }`);
                    const columnType = this.getFieldValue(this.itemData, `Column Data Type ${ i }`);

                    // if there are no column type value entered in sitecore
                    // assume there's no more columns.
                    if (!columnType) break;

                    const column = {
                        label: columnName,
                        field: `column${ i }`,
                    };

                    switch (columnType) {
                        case this.columnDataType.Checkbox:
                            column.html = true;
                            column.type = 'boolean';
                            break;
                        case this.columnDataType.DateTime: {
                            const dateTimeFormat = this.getFieldValue(this.itemData, `Column Format ${ i }`);
                            column.dateInputFormat = 'yyyyMMddHHmmss';
                            column.dateOutputFormat = dateTimeFormat;
                            column.type = 'date';
                            break;
                        }
                        case this.columnDataType.Links:
                            column.html = true;
                            column.sortFn = this.stripedHtmlSort;
                            break;
                        case this.columnDataType.Text:
                            column.html = true;
                            column.sortFn = this.stripedHtmlSort;
                            break;
                        default:
                            break;
                    }
                    workingColumns.push(column);
                }
            }
            this.columns = workingColumns;
        },

        setRow() {
            const workingRows = [];
            if (!!this.itemData && !!this.itemData.children) {
                for (let rowIndex = 0; rowIndex < this.itemData.children.length; rowIndex++) {
                    const row = {
                        id: rowIndex,
                    };
                    const rowData = this.itemData.children[rowIndex];

                    let skipRow = false;
                    const now = new Date();

                    for (let columnIndex = 1; columnIndex <= this.columns.length; columnIndex++) {
                        const columnType = this.getFieldValue(this.itemData, `Column Data Type ${ columnIndex }`);

                        if (skipRow) break;

                        switch (columnType) {
                            case this.columnDataType.Checkbox: {
                                const fieldValue = this.getFieldValue(rowData, `Checkbox ${ columnIndex }`);
                                if (fieldValue === '1') {
                                    row[`column${ columnIndex }`] = getAbsolutePathUrl(this.getFieldValue(this.itemData, `Column Checkbox Display ${ columnIndex }`));
                                }
                                break;
                            }
                            case this.columnDataType.DateTime: {
                                const dateTimeFieldValue = this.getFieldValue(rowData, `Datetime ${ columnIndex }`);

                                if (this.hidePastDates) {
                                    const jsDateTime = `${ dateTimeFieldValue.substring(0, 4) }-${ dateTimeFieldValue.substring(4, 6) }-${ dateTimeFieldValue.substring(6, 8) }${ dateTimeFieldValue.substring(8, 11) }:${ dateTimeFieldValue.substring(11, 13) }:${ dateTimeFieldValue.substring(13) }`;

                                    if (now > new Date(jsDateTime)) {
                                        skipRow = true;
                                    }
                                }

                                row[`column${ columnIndex }`] = dateTimeFieldValue
                                    .replace('T', '')
                                    .replace('Z', '');
                                break;
                            }
                            case this.columnDataType.Links: {
                                const anchor = rowData[`link${ columnIndex }`];
                                row[`column${ columnIndex }`] = `<a href="${ anchor.url }${ anchor.queryString ? `?${ anchor.queryString }` : '' }" target="${ anchor.target }">${ anchor.text }</a>`;
                                break;
                            }
                            case this.columnDataType.Text:
                                row[`column${ columnIndex }`] = getAbsolutePathUrl(this.getFieldValue(rowData, `Text ${ columnIndex }`));
                                break;
                            default:
                                break;
                        }
                    }

                    if (!skipRow) {
                        workingRows.push(row);
                    }
                }
            }
            this.rows = workingRows;
        },

        stripHtml(str) {
            return str.replace(/<[^>]+>/g, '');
        },

        stripedHtmlSort(row1, row2) {
            const row1Text = this.stripHtml(row1);
            const row2Text = this.stripHtml(row2);

            if (row1Text > row2Text) return 1;

            if (row1Text < row2Text) return -1;

            return 0;
        },

        forceRerender() {
            this.componentKey += 1;
        },
    },
};
</script>

<style lang="scss">
.vgt-table.vgt-table {
    background-color: transparent;

    thead th {
        background: transparent;
        color: inherit;
    }

    .o-table--shading & tr:nth-of-type(odd) td {
        background-color: #f1f1f1;
    }

    td {
        color: inherit;
    }

    th p,
    th div {
        @apply f-font-default;
        font-size: 1.125rem;
    }

    strong:first-child:last-child {
        font-size: 1.125rem;
        margin-top: 2em;
        display: block;
    }

    img {
        display: inline-block;
    }
}
</style>
